import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/lib/store";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Minus, Plus, X } from "lucide-react";

export default function CartDrawer() {
  const { t } = useTranslation();
  const { items, subtotal, updateQty, removeItem, open, setOpen } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 80 || subtotal === 0 ? 0 : 5.9;
  const total = subtotal + shipping;

  const toCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md rounded-none border-l flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b">
          <SheetTitle className="font-mono-caps text-left flex items-center justify-between">
            <span>{t("cart.title")} · {items.length}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="p-12 text-center space-y-4">
              <p className="font-serif text-2xl">{t("cart.empty")}</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="inline-block bg-navy text-cream px-6 py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors" data-testid="cart-empty-cta">
                {t("cart.empty_cta")}
              </Link>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={`${it.product_id}-${it.size_ml}`} className="p-6 flex gap-4" data-testid={`cart-item-${it.product_id}-${it.size_ml}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-caps text-xs text-muted-foreground">{it.brand}</div>
                    <div className="font-serif text-lg leading-tight">{it.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{it.size_ml}ml</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-border">
                        <button onClick={() => updateQty(it.product_id, it.size_ml, it.qty - 1)} className="p-2 hover:bg-muted" aria-label="-" data-testid={`cart-decrement-${it.product_id}`}>
                          <Minus strokeWidth={1.25} className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm">{it.qty}</span>
                        <button onClick={() => updateQty(it.product_id, it.size_ml, it.qty + 1)} className="p-2 hover:bg-muted" aria-label="+" data-testid={`cart-increment-${it.product_id}`}>
                          <Plus strokeWidth={1.25} className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="text-sm font-medium">${(it.price * it.qty).toFixed(2)}</div>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it.product_id, it.size_ml)} className="text-muted-foreground hover:text-foreground" aria-label={t("cart.remove")} data-testid={`cart-remove-${it.product_id}`}>
                    <X strokeWidth={1.25} className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-6 space-y-4">
            <div className="flex justify-between text-sm">
              <span>{t("cart.subtotal")}</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{t("cart.shipping")}</span>
              <span>{shipping === 0 ? t("cart.free") : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-serif text-2xl border-t pt-4">
              <span>{t("cart.total")}</span>
              <span data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <button
              onClick={toCheckout}
              className="w-full bg-navy text-cream py-4 font-mono-caps hover:bg-cream hover:text-navy transition-colors"
              data-testid="cart-checkout-btn"
            >
              {t("cart.checkout")}
            </button>
            <p className="text-xs text-center text-muted-foreground">
              {t("cart.ships_only_us")}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
